import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Box, Typography, TextField, Stack, ButtonBase,
    CircularProgress, InputAdornment,
    IconButton, Button,
} from '@mui/material';
import ChevronLeftRounded from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/sk';
import 'dayjs/locale/cs';
import 'dayjs/locale/en';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import employee_service from '../services/employee.service';
import workplace_service from '../services/workplace.service';
import attendance_service from '../services/attendance.service';
import EmployeeHistoryDrawer from './EmployeeHistoryDrawer';
import AddEmployeeDrawer from './AddEmployeeDrawer';
import FilterDrawer from './FilterDrawer';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import Popover from '@mui/material/Popover';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import SortDrawer from './SortDrawer';
import SortIcon from '@mui/icons-material/Sort';
import Chip from '@mui/material/Chip';
import { usePreferences } from '../contexts/PreferencesContext';
import EmployeeDayList, { summarizeDay } from './employee/EmployeeDayList';
import { DASH } from './dashboard/dashboardTokens';
import { Panel } from './dashboard/Surface';

/**
 * Tlačidlo, ktoré nekričí, kým nie je zapnuté. Modré vyplnené tlačidlo pre
 * „Filter" hlásilo dôležitosť, ktorú filter nemá — dôležitý je zoznam pod ním.
 */
const QuietButton = ({ onClick, icon, active, children }) => (
    <ButtonBase
        onClick={onClick}
        sx={(theme) => ({
            height: 38,
            px: 1.75,
            gap: 0.875,
            borderRadius: '999px',
            fontSize: '0.8125rem',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            color: active ? theme.palette.text.primary : theme.palette.text.secondary,
            bgcolor: active ? 'action.selected' : 'action.hover',
            '&:hover': { bgcolor: 'action.selected' },
            '&:focus-visible': { outline: `2px solid ${theme.palette.text.primary}`, outlineOffset: 2 },
        })}
    >
        {icon}
        {children}
    </ButtonBase>
);

const DAYJS_LOCALE = { SK: 'sk', EN: 'en', CZ: 'cs' };

const formatEmployeeListDate = (date, language, compact = false) => {
    const locale = DAYJS_LOCALE[language] || 'sk';
    const d = dayjs(date).locale(locale);
    if (compact) return d.format('D.M.YY');
    return d.format('dddd, D. MMMM YYYY');
};

const EmployeeList = () => {
    const { t, language } = usePreferences();
    const [searchParams] = useSearchParams();
    const [employees, setEmployees] = useState([]);
    const [workplaces, setWorkplaces] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedPosition, setSelectedPosition] = useState('All');
    const [selectedSort, setSelectedSort] = useState('worked_today');
    const [selectedNetTimeFilter, setSelectedNetTimeFilter] = useState('All');
    const [selectedScanMethodFilter, setSelectedScanMethodFilter] = useState('All');
    const [isAddDrawerOpen, setAddDrawerOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isFilterDrawerOpen, setFilterDrawerOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [attendanceData, setAttendanceData] = useState([]);
    const [insightsPeriod, setInsightsPeriod] = useState(30);
    const [insightsByEmployeeId, setInsightsByEmployeeId] = useState({});
    const [insightsMeta, setInsightsMeta] = useState(null);
    const [insightsLoading, setInsightsLoading] = useState(true);

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [activeTab, setActiveTab] = useState('timeline');

    // State pre popover
    const [calendarAnchorEl, setCalendarAnchorEl] = useState(null);
    const handleCalendarClick = (event) => setCalendarAnchorEl(event.currentTarget);
    const handleCalendarClose = () => setCalendarAnchorEl(null);
    const calendarOpen = Boolean(calendarAnchorEl);

    const [isSortDrawerOpen, setSortDrawerOpen] = useState(false);

    useEffect(() => {
        const dateParam = searchParams.get('date');
        if (!dateParam || !/^\d{4}-\d{2}-\d{2}$/.test(dateParam)) return;
        const parsed = dayjs(dateParam, 'YYYY-MM-DD', true);
        if (parsed.isValid()) {
            setSelectedDate(parsed);
            setSelectedSort('worked_today');
        }
    }, [searchParams]);




    const fetchInsights = useCallback(async () => {
        setInsightsLoading(true);
        try {
            const res = await employee_service.getInsights(insightsPeriod);
            setInsightsByEmployeeId(res.data?.byEmployeeId || {});
            setInsightsMeta({
                period: res.data?.period,
                rangeFrom: res.data?.rangeFrom,
                rangeTo: res.data?.rangeTo,
            });
        } catch (err) {
            console.error('Error fetching employee insights', err);
            setInsightsByEmployeeId({});
            setInsightsMeta(null);
        } finally {
            setInsightsLoading(false);
        }
    }, [insightsPeriod]);

    const fetchAttendanceData = useCallback(async () => {
        try {
            const attendanceRes = await attendance_service.getEmployeesWithStatus(selectedDate.format('YYYY-MM-DD'));
            setAttendanceData(attendanceRes.data);
        } catch (err) {
            console.error("Error fetching attendance data", err);
        }
    }, [selectedDate]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [empRes, workRes, attendanceRes] = await Promise.all([
                employee_service.getEmployees(),
                workplace_service.getWorkplaces(),
                attendance_service.getEmployeesWithStatus(selectedDate.format('YYYY-MM-DD'))
            ]);
            setEmployees(empRes.data);
            setWorkplaces(workRes.data);
            setAttendanceData(attendanceRes.data);
        } catch (err) {
            console.error("Error fetching data", err);
        } finally {
            setLoading(false);
        }
    }, [selectedDate]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        fetchInsights();
    }, [fetchInsights]);

    useEffect(() => {
        if (selectedDate) {
            fetchAttendanceData();
        }
    }, [selectedDate, fetchAttendanceData]);

    const handleOpenAddDrawer = () => {
        setAddDrawerOpen(true);
    };

    const handleCloseAddDrawer = () => {
        setAddDrawerOpen(false);
    };

    const handleEmployeeCreated = () => {
        fetchData();
        handleCloseAddDrawer();
    };

    const positions = ['All', ...new Set(employees.map(emp => emp.position).filter(Boolean))];

    const handleOpenDrawer = (employee, tab = 'timeline') => {
        setSelectedEmployee(employee);
        setActiveTab(tab);
        setDrawerOpen(true);
    };

    const handleCloseDrawer = () => {
        setDrawerOpen(false);
        setSelectedEmployee(null);
        setActiveTab('timeline');
    };

    const handleEmployeeDeleted = (employeeId) => {
        setEmployees(prevEmployees => prevEmployees.filter(emp => emp.id !== employeeId));
        handleCloseDrawer();
    };

    const handleEmployeeUpdated = (updatedEmployee) => {
        setEmployees(prevEmployees =>
            prevEmployees.map(emp => emp.id === updatedEmployee.id ? { ...emp, ...updatedEmployee } : emp)
        );
        // Also update the selectedEmployee to reflect changes immediately in the drawer
        setSelectedEmployee(prevSelected => prevSelected ? { ...prevSelected, ...updatedEmployee } : null);
    };

    const handleOpenFilterDrawer = () => setFilterDrawerOpen(true);
    const handleCloseFilterDrawer = () => setFilterDrawerOpen(false);

    const handleApplyFilters = (newPosition, newDate, newSort, newNetTimeFilter, newScanMethodFilter) => {
        setSelectedPosition(newPosition);
        if (newDate) {
            setSelectedDate(newDate);
        }
        if (newSort) {
            setSelectedSort(newSort);
        }
        if (newNetTimeFilter) {
            setSelectedNetTimeFilter(newNetTimeFilter);
        }
        if (newScanMethodFilter) {
            setSelectedScanMethodFilter(newScanMethodFilter);
        }
        handleCloseFilterDrawer();
    };

    const handleOpenSortDrawer = () => setSortDrawerOpen(true);
    const handleCloseSortDrawer = () => setSortDrawerOpen(false);
    const handleApplySort = (sortValue) => setSelectedSort(sortValue);

    const sortEmployees = (employeesToSort) => {
        const employeesWithAttendance = employeesToSort.map(emp => {
            const attendanceInfo = attendanceData.find(att => att.employee_id === emp.id);
            return {
                ...emp,
                attendance_records: attendanceInfo ? attendanceInfo.attendance_records : []
            };
        });

        switch (selectedSort) {
            case 'worked_today':
                // Default: tí čo pracovali dnes na začiatku
                return employeesWithAttendance.sort((a, b) => {
                    const aWorked = a.attendance_records.length > 0;
                    const bWorked = b.attendance_records.length > 0;
                    if (aWorked && !bWorked) return -1;
                    if (!aWorked && bWorked) return 1;
                    return 0;
                });

            case 'alphabetical':
                return employeesWithAttendance.sort((a, b) => a.name.localeCompare(b.name));

            case 'earliest_arrival':
                return employeesWithAttendance.sort((a, b) => {
                    const aArrival = a.attendance_records[0]?.start_time;
                    const bArrival = b.attendance_records[0]?.start_time;
                    if (!aArrival && !bArrival) return 0;
                    if (!aArrival) return 1;
                    if (!bArrival) return -1;
                    return new Date(aArrival) - new Date(bArrival);
                });

            case 'latest_arrival':
                return employeesWithAttendance.sort((a, b) => {
                    const aArrival = a.attendance_records[0]?.start_time;
                    const bArrival = b.attendance_records[0]?.start_time;
                    if (!aArrival && !bArrival) return 0;
                    if (!aArrival) return 1;
                    if (!bArrival) return -1;
                    return new Date(bArrival) - new Date(aArrival);
                });

            case 'most_breaks':
                return employeesWithAttendance.sort((a, b) => {
                    const aBreaks = a.attendance_records[0]?.breaks?.length || 0;
                    const bBreaks = b.attendance_records[0]?.breaks?.length || 0;
                    return bBreaks - aBreaks;
                });

            case 'longest_work':
                const presentL = employeesWithAttendance.filter(emp =>
                    emp.attendance_records[0]?.start_time && emp.attendance_records[0]?.end_time
                );
                const absentL = employeesWithAttendance.filter(emp =>
                    !(emp.attendance_records[0]?.start_time && emp.attendance_records[0]?.end_time)
                );
                presentL.sort((a, b) => {
                    const aTotalTime = new Date(a.attendance_records[0].end_time) - new Date(a.attendance_records[0].start_time);
                    const aBreakTime = a.attendance_records[0]?.breaks ?
                        a.attendance_records[0].breaks.reduce((acc, br) =>
                            acc + ((br.break_start_time && br.break_end_time) ?
                                (new Date(br.break_end_time) - new Date(br.break_start_time)) : 0), 0) : 0;
                    const aWorkTime = aTotalTime - aBreakTime;

                    const bTotalTime = new Date(b.attendance_records[0].end_time) - new Date(b.attendance_records[0].start_time);
                    const bBreakTime = b.attendance_records[0]?.breaks ?
                        b.attendance_records[0].breaks.reduce((acc, br) =>
                            acc + ((br.break_start_time && br.break_end_time) ?
                                (new Date(br.break_end_time) - new Date(br.break_start_time)) : 0), 0) : 0;
                    const bWorkTime = bTotalTime - bBreakTime;

                    return bWorkTime - aWorkTime;
                });
                return [...presentL, ...absentL];

            case 'shortest_work':
                // Najprv vyfiltruj len tých, čo majú príchod aj odchod
                const present = employeesWithAttendance.filter(emp =>
                    emp.attendance_records[0]?.start_time && emp.attendance_records[0]?.end_time
                );
                const absent = employeesWithAttendance.filter(emp =>
                    !(emp.attendance_records[0]?.start_time && emp.attendance_records[0]?.end_time)
                );
                present.sort((a, b) => {
                    const aTotalTime = new Date(a.attendance_records[0].end_time) - new Date(a.attendance_records[0].start_time);
                    const aBreakTime = a.attendance_records[0]?.breaks ?
                        a.attendance_records[0].breaks.reduce((acc, br) =>
                            acc + ((br.break_start_time && br.break_end_time) ?
                                (new Date(br.break_end_time) - new Date(br.break_start_time)) : 0), 0) : 0;
                    const aWorkTime = aTotalTime - aBreakTime;

                    const bTotalTime = new Date(b.attendance_records[0].end_time) - new Date(b.attendance_records[0].start_time);
                    const bBreakTime = b.attendance_records[0]?.breaks ?
                        b.attendance_records[0].breaks.reduce((acc, br) =>
                            acc + ((br.break_start_time && br.break_end_time) ?
                                (new Date(br.break_end_time) - new Date(br.break_start_time)) : 0), 0) : 0;
                    const bWorkTime = bTotalTime - bBreakTime;

                    return aWorkTime - bWorkTime;
                });
                // Najprv prítomní, potom absent
                return [...present, ...absent];

            case 'longest_break_time':
                return employeesWithAttendance.sort((a, b) => {
                    const aBreakTime = a.attendance_records[0]?.breaks
                        ? a.attendance_records[0].breaks.reduce((acc, br) =>
                            acc + ((br.break_start_time && br.break_end_time)
                                ? (new Date(br.break_end_time) - new Date(br.break_start_time))
                                : 0), 0)
                        : 0;
                    const bBreakTime = b.attendance_records[0]?.breaks
                        ? b.attendance_records[0].breaks.reduce((acc, br) =>
                            acc + ((br.break_start_time && br.break_end_time)
                                ? (new Date(br.break_end_time) - new Date(br.break_start_time))
                                : 0), 0)
                        : 0;
                    return bBreakTime - aBreakTime;
                });

            default:
                return employeesWithAttendance;
        }
    };

    const calculateNetTime = (attendanceRecords) => {
        if (!attendanceRecords || attendanceRecords.length === 0) return 0;

        const record = attendanceRecords[0];
        if (!record.start_time || !record.end_time) return 0;

        const totalTime = new Date(record.end_time) - new Date(record.start_time);
        const breakTime = record.breaks ?
            record.breaks.reduce((acc, br) =>
                acc + ((br.break_start_time && br.break_end_time) ?
                    (new Date(br.break_end_time) - new Date(br.break_start_time)) : 0), 0) : 0;

        return totalTime - breakTime;
    };

    const filteredEmployees = employees.filter(emp => {
        const matchesPosition = selectedPosition === 'All' || emp.position === selectedPosition;
        const matchesSearch = !searchQuery ||
            emp.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (emp.position && emp.position.toLowerCase().includes(searchQuery.toLowerCase())) ||
            emp.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (emp.phone_number && emp.phone_number.includes(searchQuery));

        // Net time filter
        const attendanceInfo = attendanceData.find(att => att.employee_id === emp.id);
        const netTime = calculateNetTime(attendanceInfo?.attendance_records);
        const eightHoursMs = 8 * 60 * 60 * 1000; // 8 hodín v milisekundách

        let matchesNetTime = true;
        if (selectedNetTimeFilter === 'Over 8h') {
            matchesNetTime = netTime > eightHoursMs;
        } else if (selectedNetTimeFilter === 'Under 8h') {
            matchesNetTime = netTime > 0 && netTime <= eightHoursMs;
        }
        // 'All' matches everything

        // Scan method filter
        let matchesScanMethod = true;
        if (selectedScanMethodFilter !== 'All') {
            // Ak nemá žiadne záznamy, nezhoduje sa s žiadnym scan method filtrom
            if (!attendanceInfo?.attendance_records?.length) {
                matchesScanMethod = false;
            } else {
                const record = attendanceInfo.attendance_records[0];
                if (selectedScanMethodFilter === 'NFC') {
                    matchesScanMethod = (record.scan_type_start === 'nfc' || record.scan_type_end === 'nfc');
                } else if (selectedScanMethodFilter === 'QR') {
                    matchesScanMethod = (record.scan_type_start === 'qr_code' || record.scan_type_end === 'qr_code');
                }
            }
        }

        return matchesPosition && matchesSearch && matchesNetTime && matchesScanMethod;
    });

    // Zlúčiť employee dáta s attendance dátami a zoradiť
    const employeesWithTimeline = sortEmployees(filteredEmployees);

    const today = dayjs().startOf('day');
    const isDateActive = !selectedDate.isSame(today, 'day');

    const hasFilters = selectedPosition !== 'All'
        || selectedNetTimeFilter !== 'All'
        || selectedScanMethodFilter !== 'All';

    // Jedna veta o dni pod nadpisom — koľko ľudí je v práci, na prestávke,
    // hotových a koľko sa vôbec neukázalo.
    const daySummary = summarizeDay(employeesWithTimeline);
    const daySummaryLine = [
        ['working', daySummary.working],
        ['onBreak', daySummary.onBreak],
        ['done', daySummary.done],
        ['away', daySummary.away],
    ]
        .filter(([, value]) => value > 0)
        .map(([key, value]) => `${value} ${t(`pages.employees.list.state.${key}`)}`)
        .concat(daySummary.totalWorkedStr
            ? [t('pages.employees.list.totalWorked').replace('{value}', daySummary.totalWorkedStr)]
            : [])
        .join('  ·  ');
    // Handler na zrušenie jednotlivých filtrov/sort/dátumu
    const handleDeletePosition = () => setSelectedPosition('All');
    const handleDeleteDate = () => setSelectedDate(today);
    const handleDeleteNetTimeFilter = () => setSelectedNetTimeFilter('All');
    const handleDeleteScanMethodFilter = () => setSelectedScanMethodFilter('All');
    const handleDeleteSearch = () => setSearchQuery('');

    // Mapping of sort keys to user-friendly English labels
    const sortLabels = {
        worked_today: 'Worked today',
        alphabetical: 'Alphabetical',
        earliest_arrival: 'Earliest arrival',
        latest_arrival: 'Latest arrival',
        most_breaks: 'Most breaks',
        longest_work: 'Longest work',
        shortest_work: 'Shortest work',
        longest_break_time: 'Longest break time',
    };
    const getSortLabel = (key) => sortLabels[key] || key;

    if (loading) {
        return <CircularProgress sx={{ display: 'block', margin: 'auto' }} />;
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={DAYJS_LOCALE[language] || 'sk'}>
            <Box sx={{ margin: 'auto', maxWidth: 1200, bgcolor: 'background.default' }}>
                {/* Deň je os, na ktorej celá stránka visí. Doteraz sedel v malom
                    tlačidle vpravo v paneli nástrojov — teraz je to nadpis. */}
                <Box sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 2.5, sm: 3.5 }, pb: 2.5 }}>
                    <Stack
                        direction="row"
                        alignItems="flex-start"
                        justifyContent="space-between"
                        spacing={2}
                        flexWrap="wrap"
                        useFlexGap
                    >
                        <Box sx={{ minWidth: 0 }}>
                            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ ml: '-8px' }}>
                                <IconButton
                                    size="small"
                                    onClick={() => setSelectedDate(selectedDate.subtract(1, 'day'))}
                                    aria-label={t('pages.employees.list.previousDay')}
                                >
                                    <ChevronLeftRounded fontSize="small" />
                                </IconButton>
                                <Typography
                                    component="h1"
                                    sx={{
                                        fontFamily: DASH.display,
                                        fontSize: { xs: '1.5rem', sm: '2rem' },
                                        fontWeight: 700,
                                        letterSpacing: '-0.03em',
                                        lineHeight: 1.1,
                                        textTransform: 'capitalize',
                                    }}
                                >
                                    {formatEmployeeListDate(selectedDate, language)}
                                </Typography>
                                <IconButton
                                    size="small"
                                    disabled={!isDateActive}
                                    onClick={() => setSelectedDate(selectedDate.add(1, 'day'))}
                                    aria-label={t('pages.employees.list.nextDay')}
                                >
                                    <ChevronRightRounded fontSize="small" />
                                </IconButton>
                                {isDateActive && (
                                    <Button
                                        onClick={handleDeleteDate}
                                        size="small"
                                        sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 600, ml: 0.5 }}
                                    >
                                        {t('pages.employees.list.today')}
                                    </Button>
                                )}
                                <IconButton
                                    size="small"
                                    onClick={handleCalendarClick}
                                    aria-label={t('pages.employees.list.pickDay')}
                                >
                                    <CalendarTodayIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </Stack>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                                {daySummaryLine}
                            </Typography>
                        </Box>

                        <Button
                            onClick={handleOpenAddDrawer}
                            variant="contained"
                            disableElevation
                            startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                            sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 650, px: 2.25, height: 40 }}
                        >
                            {t('pages.employees.addEmployee')}
                        </Button>
                    </Stack>

                    <Popover
                        open={calendarOpen}
                        anchorEl={calendarAnchorEl}
                        onClose={handleCalendarClose}
                        keepMounted={false}
                        disableRestoreFocus
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        PaperProps={{ sx: { borderRadius: `${DASH.radiusSm}px` } }}
                    >
                        <Box sx={{ p: 1 }}>
                            <StaticDatePicker
                                displayStaticWrapperAs="desktop"
                                value={selectedDate}
                                onChange={(date) => {
                                    setSelectedDate(date);
                                    handleCalendarClose();
                                }}
                                slotProps={{ actionBar: { actions: [] } }}
                            />
                        </Box>
                    </Popover>
                </Box>

                <Box sx={{ px: { xs: 2, sm: 3 }, pb: 3 }}>
                    <Panel padded={false}>
                        <Box
                            sx={{
                                px: { xs: 2, sm: 2.5 },
                                py: 1.75,
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                flexWrap: 'wrap',
                            }}
                        >
                            <TextField
                                placeholder={t('pages.employees.searchPlaceholder')}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                size="small"
                                sx={{
                                    flex: '1 1 220px',
                                    minWidth: 0,
                                    '& .MuiOutlinedInput-root': {
                                        height: 38,
                                        borderRadius: '999px',
                                        fontSize: '0.875rem',
                                        bgcolor: 'action.hover',
                                        '& fieldset': { border: 'none' },
                                        '&.Mui-focused': {
                                            bgcolor: 'background.paper',
                                            boxShadow: (theme) => `0 0 0 1.5px ${theme.palette.text.primary}`,
                                        },
                                    },
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                                <QuietButton
                                    onClick={handleOpenFilterDrawer}
                                    icon={<FilterListIcon sx={{ fontSize: 17 }} />}
                                    active={hasFilters}
                                >
                                    {t('common.filter')}
                                </QuietButton>
                                <QuietButton
                                    onClick={handleOpenSortDrawer}
                                    icon={<SortIcon sx={{ fontSize: 17 }} />}
                                    active={selectedSort !== 'worked_today'}
                                >
                                    {selectedSort !== 'worked_today' ? getSortLabel(selectedSort) : t('common.sort')}
                                </QuietButton>
                            </Stack>
                        </Box>

                        {(searchQuery || selectedPosition !== 'All' || selectedNetTimeFilter !== 'All'
                            || selectedScanMethodFilter !== 'All') && (
                            <Stack
                                direction="row"
                                flexWrap="wrap"
                                useFlexGap
                                spacing={1}
                                sx={{ px: { xs: 2, sm: 2.5 }, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}
                            >
                                {[
                                    searchQuery && { key: 'q', label: `„${searchQuery}"`, onDelete: handleDeleteSearch },
                                    selectedPosition !== 'All' && { key: 'pos', label: selectedPosition, onDelete: handleDeletePosition },
                                    selectedNetTimeFilter !== 'All' && { key: 'net', label: selectedNetTimeFilter, onDelete: handleDeleteNetTimeFilter },
                                    selectedScanMethodFilter !== 'All' && { key: 'scan', label: selectedScanMethodFilter, onDelete: handleDeleteScanMethodFilter },
                                ].filter(Boolean).map((chip) => (
                                    <Chip
                                        key={chip.key}
                                        label={chip.label}
                                        onDelete={chip.onDelete}
                                        size="small"
                                        sx={{ borderRadius: '999px', bgcolor: 'action.hover', fontWeight: 500 }}
                                    />
                                ))}
                            </Stack>
                        )}

                        <EmployeeDayList
                            employees={employeesWithTimeline}
                            insightsByEmployeeId={insightsByEmployeeId}
                            insightsLoading={insightsLoading}
                            loading={false}
                            t={t}
                            language={language}
                            onOpen={(emp) => handleOpenDrawer(emp)}
                            onNotify={(emp) => handleOpenDrawer(emp, 'notification')}
                            emptyTitle={t('pages.employees.list.emptyTitle')}
                            emptyHint={t('pages.employees.list.emptyHint')}
                        />
                    </Panel>
                </Box>

                <FilterDrawer
                    open={isFilterDrawerOpen}
                    onClose={handleCloseFilterDrawer}
                    positions={positions}
                    selectedPosition={selectedPosition}
                    selectedNetTimeFilter={selectedNetTimeFilter}
                    selectedScanMethodFilter={selectedScanMethodFilter}
                    onApplyFilters={handleApplyFilters}
                    employees={employees}
                    searchQuery={searchQuery}
                    selectedDate={selectedDate}
                    attendanceData={attendanceData}
                />
                <SortDrawer
                    open={isSortDrawerOpen}
                    onClose={handleCloseSortDrawer}
                    selectedSort={selectedSort}
                    onApplySort={handleApplySort}
                />
                <AddEmployeeDrawer
                    open={isAddDrawerOpen}
                    onClose={handleCloseAddDrawer}
                    workplaces={workplaces}
                    onEmployeeCreated={handleEmployeeCreated}
                    onEmployeeDeleted={handleEmployeeDeleted}
                    onEmployeeUpdated={handleEmployeeUpdated}
                />
                <EmployeeHistoryDrawer
                    employee={selectedEmployee}
                    open={drawerOpen}
                    onClose={handleCloseDrawer}
                    onEmployeeDeleted={handleEmployeeDeleted}
                    onEmployeeUpdated={handleEmployeeUpdated}
                    activeTab={activeTab}
                    selectedDate={selectedDate}
                    onRecordUpdate={fetchData}
                    insightsPeriod={insightsPeriod}
                    onInsightsPeriodChange={setInsightsPeriod}
                    insightsByEmployeeId={insightsByEmployeeId}
                    insightsMeta={insightsMeta}
                />
            </Box>
        </LocalizationProvider>
    );
};

export default EmployeeList; 